import { apiClient } from '../lib/apiClient';
import { API_ENDPOINTS } from '../lib/apiConfig';
import { useAuthStore } from '../stores/authStore';

export type TaskStatusGroup = 'running' | 'completed' | 'failed' | 'cancelled';

export interface TaskListItem {
  id: string;
  status: string;
  type: string;
  userId?: number;
  modelId?: number;
  prompt?: string;
  content?: string;
  progress?: number;
  error?: string;
  cost: number;
  reservedCost?: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface TaskListSummary {
  running: number;
  completed: number;
  failed: number;
  cancelled: number;
  total: number;
}

export interface TaskListPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface GetUserTasksParams {
  page?: number;
  limit?: number;
  statusGroup?: TaskStatusGroup;
  taskType?: string;
}

interface UserTasksResponse {
  success: boolean;
  data: TaskListItem[];
  summary: TaskListSummary;
  pagination: TaskListPagination;
}

interface DeleteTaskResponse {
  success: boolean;
  message: string;
}

export interface UserTasksResult {
  tasks: TaskListItem[];
  summary: TaskListSummary;
  pagination: TaskListPagination;
}

function getToken(): string | null {
  return useAuthStore.getState().token;
}

function buildTaskListQuery(params: GetUserTasksParams = {}): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page || 1));
  searchParams.set('limit', String(params.limit || 20));

  if (params.statusGroup) {
    searchParams.set('statusGroup', params.statusGroup);
  }

  if (params.taskType) {
    searchParams.set('taskType', params.taskType);
  }

  return `${API_ENDPOINTS.ai.tasks}?${searchParams.toString()}`;
}

export async function getUserTasks(params: GetUserTasksParams = {}): Promise<UserTasksResult> {
  const token = getToken();

  if (!token) {
    throw new Error('未登录，请先登录');
  }

  const response = await apiClient.get<UserTasksResponse>(buildTaskListQuery(params), token);

  return {
    tasks: response.data.data,
    summary: response.data.summary,
    pagination: response.data.pagination,
  };
}

export async function deleteUserTask(taskId: string): Promise<void> {
  const token = getToken();

  if (!token) {
    throw new Error('未登录，请先登录');
  }

  await apiClient.delete<DeleteTaskResponse>(API_ENDPOINTS.ai.deleteTask(taskId), token);
}

export async function cancelUserTask(taskId: string): Promise<void> {
  const token = getToken();

  if (!token) {
    throw new Error('未登录，请先登录');
  }

  await apiClient.post<DeleteTaskResponse>(API_ENDPOINTS.ai.cancelTask(taskId), undefined, token);
}
